import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ScheduleCard } from '../posts/ScheduleCard';
import type { Post } from '../../types/calendar';

interface ScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (scheduledAtUtc: string | null, timezone?: string) => void;
  post: Post | null;
  initialDate?: Date; // Date from the calendar day where it was dropped
}

export const ScheduleModal: React.FC<ScheduleModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  post,
  initialDate
}) => {
  const { t } = useTranslation();
  
  // Create initial date preserving the time from the dropped date
  const getInitialScheduledDate = () => {
    if (!initialDate) return null;
    
    console.log('📅 ScheduleModal - getInitialScheduledDate - Received initialDate:', initialDate, 'ISO:', initialDate.toISOString());
    
    // For week view drops, preserve the exact hour; for month view, use noon
    const year = initialDate.getFullYear();
    const month = initialDate.getMonth();
    const day = initialDate.getDate();
    const hour = initialDate.getHours(); // Keep the hour from the drop
    const minute = initialDate.getMinutes(); // Keep the minute from the drop
    
    // Create the date in local time first, then convert to UTC for storage
    const localDate = new Date(year, month, day, hour, minute, 0, 0);
    const isoString = localDate.toISOString();
    
    console.log('📅 ScheduleModal - getInitialScheduledDate - Created ISO string:', isoString, 'for local time', hour + ':' + String(minute).padStart(2, '0'));
    
    return isoString;
  };
  
  const [scheduledAtUtc, setScheduledAtUtc] = useState<string | null>(getInitialScheduledDate());
  const [timezone, setTimezone] = useState<string>();

  // If there's an initial date, notify parent immediately that toggle should be on
  React.useEffect(() => {
    const initialScheduledDate = getInitialScheduledDate();
    if (initialScheduledDate) {
      setScheduledAtUtc(initialScheduledDate);
    }
  }, [initialDate]);

  const handleScheduleChange = (newScheduledAtUtc: string | null, newTimezone?: string) => {
    setScheduledAtUtc(newScheduledAtUtc);
    setTimezone(newTimezone);
  };

  const handleConfirm = () => {
    onConfirm(scheduledAtUtc, timezone);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen || !post) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleCancel}
        />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {t('modal.schedule.title', 'When do you want to publish?')}
              </h3>
              <button
                onClick={handleCancel}
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <span className="sr-only">{t('common.close', 'Close')}</span>
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Post preview */}
            <div className="mb-6 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-start gap-3">
                {post.media && (
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex-shrink-0 overflow-hidden">
                    <img 
                      src={post.media.url} 
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 line-clamp-3">
                    {post.content || 'Sans texte'}
                  </p>
                  {post.brandName && (
                    <p className="text-xs text-gray-500 mt-1">{post.brandName}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Schedule Card */}
            <ScheduleCard
              initialScheduledAtUtc={scheduledAtUtc}
              onScheduleChange={handleScheduleChange}
            />
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleConfirm}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              {t('common.confirm', 'Valider')}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              {t('common.cancel', 'Annuler')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 